import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Loader2, Info, CheckCircle } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const ApplicationFormBuilder = () => {
  const { userProfile } = useRealtime();
  const [applicationSettings, setApplicationSettings] = useState({
    requirePayment: true,
    customFields: [],
    requirements: [],
    instructions: ''
  });
  const [newCustomField, setNewCustomField] = useState({ label: '', type: 'text', required: false });
  const [newRequirement, setNewRequirement] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const institutionId = userProfile?.coachingId || userProfile?.institutionId;
    if (!institutionId) return;

    const result = await admissionsService.getInstitutionCustomization(institutionId);
    if (result.success && result.customization?.applicationSettings) {
      setApplicationSettings(result.customization.applicationSettings);
    }
  };

  const addCustomField = () => {
    if (newCustomField.label.trim()) {
      setApplicationSettings(prev => ({
        ...prev,
        customFields: [...prev.customFields, { ...newCustomField }]
      }));
      setNewCustomField({ label: '', type: 'text', required: false });
    }
  };

  const removeCustomField = (index) => {
    setApplicationSettings(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setApplicationSettings(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setApplicationSettings(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        toast.error('Please set up your institution profile first');
        return;
      }

      const customization = {
        applicationSettings
      };

      const result = await admissionsService.saveInstitutionCustomization(institutionId, customization);
      if (result.success) {
        toast.success('Application form settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Application Form Builder</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Customize your application form fields and requirements
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-6">
        {/* Default Fields Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Default Application Fields</h2>
            <Info className="w-5 h-5 text-gray-400" title="These fields are always included in the application form" />
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
              The following fields are automatically included in all application forms. These cannot be removed or modified.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Personal Information
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>First Name <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Last Name <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Date of Birth <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Gender <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Email <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Phone <span className="text-red-500">*</span></span>
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Address Information
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Street Address <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>City <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>State <span className="text-red-500">*</span></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Pincode <span className="text-red-500">*</span></span>
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Academic Information
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Previous School
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Previous Class
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Applying For Class
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Academic Year
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Parent/Guardian Information
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Parent Name
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Parent Phone
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Parent Email
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Parent Occupation
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Fields */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Additional Custom Fields</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add custom fields to collect additional information from applicants
          </p>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCustomField.label}
              onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value }))}
              placeholder="Field Label"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
            <select
              value={newCustomField.type}
              onChange={(e) => setNewCustomField(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              <option value="text">Text</option>
              <option value="textarea">Textarea</option>
              <option value="number">Number</option>
              <option value="email">Email</option>
              <option value="date">Date</option>
              <option value="photo">Photo Upload</option>
              <option value="file">File Upload</option>
            </select>
            <label className="flex items-center gap-2 px-3 py-2">
              <input
                type="checkbox"
                checked={newCustomField.required}
                onChange={(e) => setNewCustomField(prev => ({ ...prev, required: e.target.checked }))}
              />
              <span className="text-sm">Required</span>
            </label>
            <button
              onClick={addCustomField}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-2">
            {applicationSettings.customFields.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center text-gray-500 dark:text-gray-400">
                <p>No custom fields added yet. Add your first custom field above.</p>
              </div>
            ) : (
              applicationSettings.customFields.map((field, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">{field.label}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({field.type})</span>
                      {field.required && <span className="text-red-500 text-sm ml-2">*</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => removeCustomField(index)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove field"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Requirements */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Requirements</h2>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newRequirement}
              onChange={(e) => setNewRequirement(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addRequirement()}
              placeholder="Add a requirement..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
            <button
              onClick={addRequirement}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-2">
            {applicationSettings.requirements.map((req, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <span>{req}</span>
                <button
                  onClick={() => removeRequirement(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Instructions</h2>
          <textarea
            value={applicationSettings.instructions}
            onChange={(e) => setApplicationSettings(prev => ({
              ...prev,
              instructions: e.target.value
            }))}
            rows={6}
            placeholder="Add instructions for applicants..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
          />
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFormBuilder;

